import { invoke } from '@tauri-apps/api/core';
import { listen, type UnlistenFn } from '@tauri-apps/api/event';
import type { AccessType, IceServer, OrganizationRef, UserRef } from '@viewer/protocol';

export interface IceCandidatePayload {
  candidate: string;
  sdpMid: string | null;
  sdpMLineIndex: number | null;
}

export type PeerState = 'new' | 'connecting' | 'connected' | 'disconnected' | 'failed' | 'closed' | 'unknown';

export interface HostIceEvent extends IceCandidatePayload {
  sessionId: string;
}

export interface HostStateEvent {
  sessionId: string;
  state: PeerState;
}

/** Uma pessoa acessando este computador. Sem `organization`, entrou sem empresa (aceite ou senha). */
export interface HostConnection {
  sessionId: string;
  peerId: string;
  /** Nome do computador de quem acessa. */
  peerName: string;
  user: Pick<UserRef, 'name' | 'email'> | null;
  organization: OrganizationRef | null;
  access: AccessType;
  state: PeerState;
}

/** Cria o PeerConnection no Rust e devolve o SDP da oferta. Sem `allowControl`, a entrada é ignorada. */
export function hostStart(
  sessionId: string,
  iceServers: IceServer[],
  relayOnly: boolean,
  allowControl: boolean,
  allowClipboard: boolean,
  allowFileTransfer: boolean,
): Promise<string> {
  return invoke<string>('host_start', { sessionId, iceServers, relayOnly, allowControl, allowClipboard, allowFileTransfer });
}

export function hostSetAnswer(sessionId: string, sdp: string): Promise<void> {
  return invoke('host_set_answer', { sessionId, sdp });
}

export function hostAddIceCandidate(sessionId: string, candidate: IceCandidatePayload): Promise<void> {
  return invoke('host_add_ice_candidate', { sessionId, candidate });
}

/** Encerra só esta sessão; as outras pessoas continuam conectadas. */
export function hostStop(sessionId: string): Promise<void> {
  return invoke('host_stop', { sessionId });
}

export function onHostIce(handler: (event: HostIceEvent) => void): Promise<UnlistenFn> {
  return listen<HostIceEvent>('host:ice', (e) => handler(e.payload));
}

export function onHostState(handler: (event: HostStateEvent) => void): Promise<UnlistenFn> {
  return listen<HostStateEvent>('host:state', (e) => handler(e.payload));
}

/** Mostra as conexões no menu do ícone da bandeja. */
export function traySetConnections(connections: HostConnection[]): Promise<void> {
  return invoke('tray_set_connections', { connections });
}

/** "Desconectar" escolhido no menu da bandeja: uma pessoa, uma empresa ou todos. */
export function onTrayEndSessions(handler: (sessionIds: string[]) => void): Promise<UnlistenFn> {
  return listen<string[]>('host:end-sessions', (e) => handler(e.payload));
}
